import { type VideoJsPlayer } from 'video.js';

export const LIVE_CHECK_INTERVAL_MS = 5000;
export const FROZEN_AFTER_MS = 15000;
export const MAX_LIVE_LAG_SECONDS = 10;

export class LiveWatchdog {
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private lastCurrentTime = 0;
  private lastProgressAt = Date.now();

  constructor(
    private readonly player: VideoJsPlayer,
    private readonly onFrozen: () => void,
  ) {}

  start(): void {
    this.markProgress();
    this.intervalId = setInterval(this.check, LIVE_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private check = (): void => {
    if (document.hidden || this.player.paused()) {
      this.markProgress();
      return;
    }

    const currentTime = this.player.currentTime();
    if (currentTime !== this.lastCurrentTime) {
      this.lastCurrentTime = currentTime;
      this.markProgress();
    } else if (Date.now() - this.lastProgressAt >= FROZEN_AFTER_MS) {
      this.markProgress();
      this.onFrozen();
      return;
    }

    if (this.liveLagSeconds() > MAX_LIVE_LAG_SECONDS) {
      this.player.liveTracker.seekToLiveEdge();
    }
  };

  private handleVisibilityChange = (): void => {
    if (document.hidden) {
      return;
    }
    this.markProgress();
    this.player.liveTracker.seekToLiveEdge();
  };

  private liveLagSeconds(): number {
    const liveTime = this.player.liveTracker.liveCurrentTime();
    return Number.isFinite(liveTime) ? liveTime - this.player.currentTime() : 0;
  }

  private markProgress(): void {
    this.lastCurrentTime = this.player.currentTime();
    this.lastProgressAt = Date.now();
  }
}
